import React, { useState, useEffect } from 'react';
import { Plus, Droplet, Calendar, Trash2, Edit2, Save, X, AlertCircle, Leaf, StickyNote, Clock, Info } from 'lucide-react';
import ApiService from '../services/api';
import { trackPlantAdded, trackPlantWatered, trackPlantDeleted, trackNoteAdded } from '../utils/analytics';

const PlantTracker = ({ isGuestMode = false, onDataUpdate, initialPlants = [] }) => {
  const [plants, setPlants] = useState(isGuestMode ? initialPlants : []);
  const [isAddingPlant, setIsAddingPlant] = useState(false);
  const [editingPlant, setEditingPlant] = useState(null);
  const [newPlant, setNewPlant] = useState({ name: '', wateringInterval: 7 });
  const [newNote, setNewNote] = useState({});
  const [loading, setLoading] = useState(!isGuestMode);
  const [error, setError] = useState('');
  const [showGuestLimitWarning, setShowGuestLimitWarning] = useState(false);

  useEffect(() => {
    if (!isGuestMode) {
      loadPlants();
    }
  }, [isGuestMode]);

  useEffect(() => {
    if (isGuestMode && onDataUpdate) {
      onDataUpdate(plants);
    }
  }, [plants, isGuestMode, onDataUpdate]);

  const loadPlants = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getPlants();
      setPlants(data);
      setError('');
    } catch (err) {
      setError('Failed to load plants. Please try again.');
      console.error('Error loading plants:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateDaysSinceWatering = (lastWatered) => {
    if (!lastWatered) return null;
    const last = new Date(lastWatered);
    const today = new Date();
    const diffTime = Math.abs(today - last);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const calculateNextWatering = (lastWatered, interval) => {
    if (!lastWatered) return 'Not set';
    const last = new Date(lastWatered);
    const next = new Date(last.getTime() + interval * 24 * 60 * 60 * 1000);
    return next.toLocaleDateString();
  };

  const needsWatering = (plant) => {
    const days = calculateDaysSinceWatering(plant.lastWatered);
    if (days === null) return true;
    return days >= plant.wateringInterval;
  };

  const handleAddPlant = async () => {
    if (!newPlant.name) return;
    
    // Guest mode limit
    if (isGuestMode && plants.length >= 3) {
      setShowGuestLimitWarning(true);
      return;
    }
    
    try {
      if (isGuestMode) {
        // Guest mode - local only
        const plant = {
          id: Date.now(),
          ...newPlant,
          lastWatered: null,
          notes: []
        };
        setPlants([...plants, plant]);
        trackPlantAdded(newPlant.name + '_guest');
      } else {
        // Authenticated mode
        const plant = await ApiService.createPlant(newPlant);
        setPlants([...plants, plant]);
        trackPlantAdded(newPlant.name);
      }
      
      setNewPlant({ name: '', wateringInterval: 7 });
      setIsAddingPlant(false);
      setError('');
    } catch (err) {
      setError('Failed to add plant. Please try again.');
    }
  };

  const handleUpdatePlant = async (id) => {
    const plant = plants.find(p => p.id === id);
    
    try {
      if (isGuestMode) {
        // Just update local state
        setEditingPlant(null);
      } else {
        await ApiService.updatePlant(id, {
          name: plant.name,
          wateringInterval: plant.wateringInterval,
          lastWatered: plant.lastWatered
        });
        setEditingPlant(null);
      }
      setError('');
    } catch (err) {
      setError('Failed to update plant. Please try again.');
      if (!isGuestMode) {
        loadPlants(); // Reload to get original data
      }
    }
  };

  const handleDeletePlant = async (id) => {
    if (!window.confirm('Are you sure you want to delete this plant?')) return;

    try {
      const plant = plants.find(p => p.id === id);
      
      if (isGuestMode) {
        setPlants(plants.filter(p => p.id !== id));
        trackPlantDeleted(plant.name + '_guest');
      } else {
        await ApiService.deletePlant(id);
        setPlants(plants.filter(p => p.id !== id));
        trackPlantDeleted(plant.name);
      }
      setError('');
    } catch (err) {
      setError('Failed to delete plant. Please try again.');
    }
  };

  const handleWaterPlant = async (id) => {
    try {
      const plant = plants.find(p => p.id === id);
      
      if (isGuestMode) {
        const now = new Date().toISOString();
        setPlants(plants.map(p => 
          p.id === id ? { ...p, lastWatered: now } : p
        ));
        trackPlantWatered(plant.name + '_guest');
      } else {
        const result = await ApiService.waterPlant(id);
        setPlants(plants.map(p => 
          p.id === id ? { ...p, lastWatered: result.lastWatered } : p
        ));
        trackPlantWatered(plant.name);
      }
      setError('');
    } catch (err) {
      setError('Failed to update watering. Please try again.');
    }
  };

  const handleAddNote = async (plantId) => {
    const noteText = newNote[plantId];
    if (!noteText) return;
    
    try {
      const plant = plants.find(p => p.id === plantId);
      
      if (isGuestMode) {
        const note = {
          id: Date.now(),
          text: noteText,
          date: new Date().toLocaleDateString()
        };
        setPlants(plants.map(p => 
          p.id === plantId 
            ? { ...p, notes: [...(p.notes || []), note] }
            : p
        ));
        trackNoteAdded(plant.name + '_guest');
      } else {
        const note = await ApiService.addNote(plantId, noteText);
        setPlants(plants.map(p => 
          p.id === plantId 
            ? { ...p, notes: [...(p.notes || []), note] }
            : p
        ));
        trackNoteAdded(plant.name);
      }
      
      setNewNote({ ...newNote, [plantId]: '' });
      setError('');
    } catch (err) {
      setError('Failed to add note. Please try again.');
    }
  };

  const handleDeleteNote = async (noteId, plantId) => {
    try {
      if (isGuestMode) {
        setPlants(plants.map(p => 
          p.id === plantId 
            ? { ...p, notes: p.notes.filter(n => n.id !== noteId) }
            : p
        ));
      } else {
        await ApiService.deleteNote(noteId);
        setPlants(plants.map(p => 
          p.id === plantId 
            ? { ...p, notes: p.notes.filter(n => n.id !== noteId) }
            : p
        ));
      }
      setError('');
    } catch (err) {
      setError('Failed to delete note. Please try again.');
    }
  };

  const sortedPlants = [...plants].sort((a, b) => {
    const aNeedsWater = needsWatering(a);
    const bNeedsWater = needsWatering(b);
    if (aNeedsWater && !bNeedsWater) return -1;
    if (!aNeedsWater && bNeedsWater) return 1;
    return a.name.localeCompare(b.name);
  });

  const plantsNeedingWater = plants.filter(needsWatering).length;
  const wellWateredPlants = plants.length - plantsNeedingWater;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-600">Loading your plants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Leaf className="text-green-600 mr-3" size={32} />
            <h1 className="text-2xl font-bold text-gray-800">
              {isGuestMode ? 'Demo Plant Collection' : 'My Plant Collection'}
            </h1>
          </div>
          {!isAddingPlant && (
            <button
              onClick={() => setIsAddingPlant(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
            >
              <Plus size={20} className="mr-1" />
              Add Plant
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
            <AlertCircle size={20} className="mr-2" />
            {error}
          </div>
        )}

        {/* Guest Mode Info */}
        {isGuestMode && plants.length === 0 && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="text-blue-600 mt-0.5" size={20} />
              <div>
                <p className="text-blue-800 font-medium mb-1">Welcome to Demo Mode!</p>
                <p className="text-blue-700 text-sm">
                  Try adding plants and tracking their watering schedule. 
                  You can add up to 3 plants in demo mode. 
                  Create a free account to save unlimited plants!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Dashboard */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-blue-600 text-2xl font-bold">{plants.length}</div>
            <div className="text-gray-600 text-sm">Total Plants</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-red-600 text-2xl font-bold">{plantsNeedingWater}</div>
            <div className="text-gray-600 text-sm">Need Water</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-green-600 text-2xl font-bold">{wellWateredPlants}</div>
            <div className="text-gray-600 text-sm">Well Watered</div>
          </div>
        </div>

        {/* Add Plant Form */}
        {isAddingPlant && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-3">Add New Plant</h3>
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="Plant name"
                value={newPlant.name}
                onChange={(e) => setNewPlant({ ...newPlant, name: e.target.value })}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <input
                type="number"
                placeholder="Days"
                value={newPlant.wateringInterval}
                onChange={(e) => setNewPlant({ ...newPlant, wateringInterval: parseInt(e.target.value) })}
                className="w-20 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                min="1"
              />
              <button
                onClick={handleAddPlant}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Save size={20} />
              </button>
              <button
                onClick={() => {
                  setIsAddingPlant(false);
                  setNewPlant({ name: '', wateringInterval: 7 });
                }}
                className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Guest Limit Warning */}
        {showGuestLimitWarning && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="text-amber-600 mt-0.5" size={20} />
              <div>
                <p className="text-amber-800 font-medium mb-1">Demo Limit Reached</p>
                <p className="text-amber-700 text-sm">
                  You can track up to 3 plants in demo mode. 
                  Create a free account to add unlimited plants!
                </p>
                <button
                  onClick={() => setShowGuestLimitWarning(false)}
                  className="text-amber-600 hover:text-amber-700 text-sm font-medium mt-2"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Plants List */}
        <div className="space-y-4">
          {sortedPlants.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Leaf className="mx-auto mb-4 text-gray-300" size={48} />
              <p>No plants yet. Add your first plant to get started!</p>
              {isGuestMode && (
                <p className="text-sm mt-2">Try adding a plant to see how it works!</p>
              )}
            </div>
          ) : (
            sortedPlants.map(plant => (
              <div
                key={plant.id}
                className={`p-4 rounded-lg border-2 transition-all ${
                  needsWatering(plant) 
                    ? 'border-red-300 bg-red-50' 
                    : 'border-green-300 bg-green-50'
                }`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    {editingPlant === plant.id ? (
                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={plant.name}
                          onChange={(e) => setPlants(plants.map(p => 
                            p.id === plant.id ? { ...p, name: e.target.value } : p
                          ))}
                          className="flex-1 px-3 py-1 border border-gray-300 rounded"
                        />
                        <input
                          type="number"
                          value={plant.wateringInterval}
                          onChange={(e) => setPlants(plants.map(p => 
                            p.id === plant.id ? { ...p, wateringInterval: parseInt(e.target.value) } : p
                          ))}
                          className="w-20 px-3 py-1 border border-gray-300 rounded"
                          min="1"
                        />
                        <button
                          onClick={() => handleUpdatePlant(plant.id)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <Save size={18} />
                        </button>
                        <button
                          onClick={() => {
                            setEditingPlant(null);
                            if (!isGuestMode) {
                              loadPlants();
                            }
                          }}
                          className="text-gray-600 hover:text-gray-700"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <h3 className="font-semibold text-lg">{plant.name}</h3>
                        <div className="text-sm text-gray-600 mt-1 space-y-1">
                          <div className="flex items-center">
                            <Calendar size={14} className="mr-1" />
                            Water every {plant.wateringInterval} days
                          </div>
                          {plant.lastWatered && (
                            <>
                              <div className="flex items-center">
                                <Clock size={14} className="mr-1" />
                                Last watered: {calculateDaysSinceWatering(plant.lastWatered)} days ago
                              </div>
                              <div className="flex items-center">
                                <Droplet size={14} className="mr-1" />
                                Next watering: {calculateNextWatering(plant.lastWatered, plant.wateringInterval)}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleWaterPlant(plant.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-lg text-sm transition-colors flex items-center"
                    >
                      <Droplet size={16} className="mr-1" />
                      Water Now
                    </button>
                    <button
                      onClick={() => setEditingPlant(plant.id)}
                      className="text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDeletePlant(plant.id)}
                      className="text-red-600 hover:text-red-700 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center mb-2">
                    <StickyNote size={16} className="mr-1 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">Notes & Observations</span>
                  </div>
                  
                  {plant.notes && plant.notes.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {plant.notes.map(note => (
                        <div key={note.id} className="bg-white p-2 rounded text-sm flex justify-between items-start">
                          <div>
                            <span className="text-gray-600">{note.date}:</span> {note.text}
                          </div>
                          <button
                            onClick={() => handleDeleteNote(note.id, plant.id)}
                            className="text-red-500 hover:text-red-600 ml-2"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add a note..."
                      value={newNote[plant.id] || ''}
                      onChange={(e) => setNewNote({ ...newNote, [plant.id]: e.target.value })}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddNote(plant.id)}
                      className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => handleAddNote(plant.id)}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm transition-colors"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PlantTracker;